import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    untracked
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import { SiPillsInputComponent } from '@siemens/element-ng/pills-input';
import { ApplicationUser, Location, OrgUnit } from '@tran-monorepo/interfaces/applications';
import { LocationSelectorComponent } from '../../location-selector';
import { OrgUnitSelectorComponent } from '../../org-unit-selector';
import { ApplicationDetailDashboardBoxComponent } from '../application-detail-dashboard-box/application-detail-dashboard-box.component';
import { ApplicationDetailPeopleComponent } from '../application-detail-people/application-detail-people.component';
import { ApplicationInformationForm } from '../application-detail.form';

@Component({
    selector: 'tran-application-information',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ApplicationDetailDashboardBoxComponent,
        SiFormItemComponent,
        SiPillsInputComponent,
        TranslateModule,
        LocationSelectorComponent,
        ApplicationDetailPeopleComponent,
        OrgUnitSelectorComponent
    ],
    templateUrl: './application-information.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationInformationComponent {
    public readonly applicationName = input<string>('');
    public readonly requesters = input<ApplicationUser[]>([]);
    public readonly affectedLocations = input<Location[]>([]);
    public readonly involvedOrganizations = input<OrgUnit[]>([]);
    public readonly allLocations = input<Location[]>([]);
    public readonly allOrgUnits = input<OrgUnit[]>([]);
    public readonly readonly = input(false, { transform: booleanAttribute });
    public readonly isLoading = model<boolean>(false);
    public readonly heading = input<string>('components.application-detail-dashboard.application-information.heading');
    public readonly form = input<ApplicationInformationForm | null>(null);

    protected readonly localForm: ApplicationInformationForm = new FormGroup({
        applicants: new FormControl<ApplicationUser[]>([], { nonNullable: true }),
        name: new FormControl<string>('', { nonNullable: true }),
        affectedLocations: new FormControl<Location[]>([], { nonNullable: true }),
        involvedOrganizations: new FormControl<OrgUnit[]>([], { nonNullable: true })
    }) as ApplicationInformationForm;

    protected readonly affectedLocationsDisplayControl = new FormControl<string[]>([], { nonNullable: true });
    protected readonly involvedOrganizationsDisplayControl = new FormControl<string[]>([], { nonNullable: true });

    protected readonly activeForm = computed(() => this.form() ?? this.localForm);

    protected readonly affectedLocationNames = computed(() => this.affectedLocations().map(location => location.name));
    protected readonly involvedOrganizationNames = computed(() =>
        this.involvedOrganizations().map(org => org.abbreviation)
    );

    constructor() {
        this.effectPatchFormValues();
    }

    private effectPatchFormValues(): void {
        effect(() => {
            const activeForm = this.activeForm();
            const name = this.applicationName();
            const requesters = this.requesters();
            const locations = this.affectedLocations();
            const organizations = this.involvedOrganizations();
            const allLocations = this.allLocations();
            const allOrgUnits = this.allOrgUnits();

            if (activeForm && requesters && locations && organizations) {
                untracked(() => {
                    const matchedLocations = allLocations.filter(loc => locations.some(l => l.id === loc.id));
                    const matchedOrganizations = allOrgUnits.filter(org => organizations.some(o => o.id === org.id));

                    activeForm.patchValue({
                        name: name,
                        applicants: requesters,
                        affectedLocations: matchedLocations,
                        involvedOrganizations: matchedOrganizations
                    });
                });
            }
        });

        effect(() => {
            this.affectedLocationsDisplayControl.setValue(this.affectedLocationNames(), { emitEvent: false });
        });

        effect(() => {
            this.involvedOrganizationsDisplayControl.setValue(this.involvedOrganizationNames(), { emitEvent: false });
        });
    }
}
